import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { adminApi } from '@/lib/adminApi';
import { formatApiError } from '@/lib/api';
import {
  Loader2, Compass, CloudSun, Layers, GraduationCap, ArrowRight,
  ClipboardList, FileText, CheckCircle2, Clock, Gauge, Building2, Filter, Download,
  ChevronDown, ChevronRight,
  BadgeCheck, Sparkles, Heart, Wrench, BrainCircuit,
} from 'lucide-react';
import { useSort } from '@/lib/useSort';
import { SortableHeader } from '@/components/shared/SortableHeader';

// ----- presentation maps -----
const SUITE_DEF = {
  talentmap:         { label: 'TalentMap',         icon: Compass,        tone: 'primary', route: '/admin/talentmap' },
  climara:           { label: 'Climara',           icon: CloudSun,       tone: 'warning', route: '/admin/climara' },
  talentcompetency:  { label: 'TalentCompetency',  icon: Layers,         tone: 'accent',  route: '/admin/talentcompetency' },
  talenttrain:       { label: 'TalentTrain',       icon: GraduationCap,  tone: 'success', route: '/admin/talenttrain' },
  talentcheck:       { label: 'TalentCheck',       icon: BadgeCheck,     tone: 'info',    route: '/admin/talentcheck' },
  talentstart:       { label: 'TalentStart',       icon: Sparkles,       tone: 'accent',  route: '/admin/talentstart' },
  talentheart:       { label: 'TalentHeart',       icon: Heart,          tone: 'warning', route: '/admin/talentheart' },
  talentfix:         { label: 'TalentFix',         icon: Wrench,         tone: 'info',    route: '/admin/talentfix' },
  intelligence:      { label: 'TalentIntelligence',icon: BrainCircuit,   tone: 'accent',  route: '/admin/talentintelligence' },
};
const TONE_BG = {
  primary: 'bg-primary/10 text-primary',
  warning: 'bg-warning/10 text-warning',
  accent:  'bg-accent/10 text-accent',
  success: 'bg-success/10 text-success',
  info:    'bg-info/10 text-info',
};
const STATUS_TONE = {
  active:   'bg-success/10 text-success border-success/20',
  draft:    'bg-muted text-muted-foreground border-border',
  inactive: 'bg-destructive/10 text-destructive border-destructive/20',
};
const REPORT_STATUS_TONE = {
  draft_ai_generated: 'bg-info/10 text-info',
  under_review:       'bg-warning/10 text-warning',
  edited_by_hm:       'bg-warning/10 text-warning',
  approved:           'bg-primary/10 text-primary',
  released_to_client: 'bg-success/10 text-success',
  rejected:           'bg-destructive/10 text-destructive',
};
const REPORT_STATUS_LABEL = {
  draft_ai_generated: 'Draft (AI)', under_review: 'Under review',
  edited_by_hm: 'Edited', approved: 'Approved',
  released_to_client: 'Released', rejected: 'Rejected',
};

const fmtHours = (hrs) => {
  if (hrs === null || hrs === undefined) return '—';
  if (hrs < 1) return `${Math.round(hrs * 60)} min`;
  if (hrs < 48) return `${hrs.toFixed(1)} h`;
  return `${(hrs / 24).toFixed(1)} d`;
};
const fmtRelative = (iso, t) => {
  if (!iso) return '—';
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return t ? t('common.justNow') : 'just now';
    if (diff < 3600) return t ? t('common.minAgo', { n: Math.floor(diff / 60) }) : `${Math.floor(diff / 60)} min ago`;
    if (diff < 86400) return t ? t('common.hoursAgo', { n: Math.floor(diff / 3600) }) : `${Math.floor(diff / 3600)} h ago`;
    return t ? t('common.daysAgo', { n: Math.floor(diff / 86400) }) : `${Math.floor(diff / 86400)} d ago`;
  } catch { return iso; }
};

export default function AdminSuites() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [companies, setCompanies] = useState([]);

  const [fCompany, setFCompany] = useState('all');
  const [fSuite, setFSuite] = useState('all');
  const [fStatus, setFStatus] = useState('all');
  const [fFrom, setFFrom] = useState('');
  const [fTo, setFTo] = useState('');
  // BUG 09 — Comparison table is collapsible, closed by default.
  const [compareOpen, setCompareOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (fCompany !== 'all') params.company_id = fCompany;
      if (fFrom) params.date_from = new Date(fFrom).toISOString();
      if (fTo) {
        const d = new Date(fTo); d.setHours(23, 59, 59, 999);
        params.date_to = d.toISOString();
      }
      const res = await adminApi.suitesOverview(params);
      setData(res);
      if (res.companies) setCompanies(res.companies);
    } catch (e) {
      toast.error(t('toasts.failedToLoadOverview'), { description: formatApiError(e) });
    } finally {
      setLoading(false);
    }
  }, [fCompany, fFrom, fTo]);

  useEffect(() => { load(); }, [load]);

  const resetFilters = () => {
    setFCompany('all'); setFSuite('all'); setFStatus('all'); setFFrom(''); setFTo('');
  };

  // Client-side filters (suite + status) so the grid/table reflects them without re-fetch
  const shownSuitesRaw = useMemo(() => {
    if (!data) return [];
    let list = data.suites;
    if (fSuite !== 'all') list = list.filter((s) => s.code === fSuite);
    if (fStatus !== 'all') list = list.filter((s) => s.status === fStatus);
    return list;
  }, [data, fSuite, fStatus]);

  // Sort for the comparison table
  const suiteSort = useSort();
  const shownSuites = useMemo(() => suiteSort.apply(shownSuitesRaw, {
    suite:        (s) => (s.name || '').toLowerCase(),
    status:       (s) => (s.status || '').toLowerCase(),
    activeQs:     (s) => s.active_questionnaires || 0,
    assignments:  (s) => s.assignments_total || 0,
    completion:   (s) => s.completion_pct || 0,
    reports:      (s) => s.reports_total || 0,
    released:     (s) => s.reports_released || 0,
    pending:      (s) => s.reports_pending || 0,
    avgScore:     (s) => s.avg_score || 0,
    ttr:          (s) => s.avg_time_to_release_hours || 0,
  }), [shownSuitesRaw, suiteSort]);

  const shownActivity = useMemo(() => {
    if (!data) return [];
    let list = data.recent_activity;
    if (fSuite !== 'all') list = list.filter((ev) => ev.suite_code === fSuite);
    return list;
  }, [data, fSuite]);

  if (loading && !data) {
    return (
      <>
        <Topbar title={t('suites.overviewTitle')} subtitle={t('common.loading')} />
        <div className="p-12 flex items-center justify-center text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> {t('common.loading')}
        </div>
      </>
    );
  }

  const tt = data?.totals || { assignments: 0, submitted: 0, in_progress: 0, reports: 0, released: 0 };
  const overallCompletion = tt.assignments ? Math.round((tt.submitted / tt.assignments) * 100) : 0;

  return (
    <>
      <Topbar title={t('suites.overviewTitle')} subtitle={t('suites.overviewSubtitle')} />

      <div className="p-6 lg:p-8 space-y-6" data-testid="admin-suites-overview">

        {/* Filters */}
        <Card className="p-5 shadow-card">
          <div className="flex items-center gap-2 mb-4 text-muted-foreground">
            <Filter className="h-4 w-4" />
            <p className="text-xs uppercase tracking-wider font-semibold">{t('common.filters')}</p>
          </div>
          <div className="grid md:grid-cols-5 gap-3">
            <div>
              <Label className="text-xs text-muted-foreground">{t('common.company')}</Label>
              <Select value={fCompany} onValueChange={setFCompany}>
                <SelectTrigger data-testid="filter-company" className="mt-1 h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('common.all')}</SelectItem>
                  {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">{t('sidebar.suites')}</Label>
              <Select value={fSuite} onValueChange={setFSuite}>
                <SelectTrigger data-testid="filter-suite" className="mt-1 h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('common.all')}</SelectItem>
                  {Object.entries(SUITE_DEF).map(([code, def]) => (
                    <SelectItem key={code} value={code}>{def.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">{t('common.status')}</Label>
              <Select value={fStatus} onValueChange={setFStatus}>
                <SelectTrigger data-testid="filter-status" className="mt-1 h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('common.all')}</SelectItem>
                  <SelectItem value="active">{t('common.active')}</SelectItem>
                  <SelectItem value="draft">{t('common.draft')}</SelectItem>
                  <SelectItem value="inactive">{t('common.inactive')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">{t('common.from')}</Label>
              <Input type="date" data-testid="filter-date-from" value={fFrom} onChange={(e) => setFFrom(e.target.value)} className="mt-1 h-10" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">{t('common.to')}</Label>
              <Input type="date" data-testid="filter-date-to" value={fTo} onChange={(e) => setFTo(e.target.value)} className="mt-1 h-10" />
            </div>
          </div>
          <div className="flex justify-end mt-3 gap-2">
            <Button variant="ghost" size="sm" onClick={resetFilters} data-testid="filter-reset">{t('common.reset')}</Button>
            <Button size="sm" onClick={load} disabled={loading} className="gap-1.5" data-testid="filter-apply">
              {loading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {t('common.apply')}
            </Button>
          </div>
        </Card>

        {/* Totals — clickable: Assignments + Submitted + In Progress + Reports + Released
            all deep-link into the global list pre-filtered. */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <TotalCard
            icon={ClipboardList}
            label={t('suites.totals.assignments')}
            value={tt.assignments}
            tone="primary"
            testid="suites-total-assignments"
            onClick={() => navigate('/admin/evaluations')}
          />
          <TotalCard
            icon={CheckCircle2}
            label={t('suites.totals.submitted')}
            value={tt.submitted}
            tone="success"
            sub={t('suites.totals.completion', { n: overallCompletion })}
            testid="suites-total-submitted"
            onClick={() => navigate('/admin/evaluations?status=submitted')}
          />
          <TotalCard
            icon={Clock}
            label={t('suites.totals.inProgress')}
            value={tt.in_progress}
            tone="warning"
            testid="suites-total-in-progress"
            onClick={() => navigate('/admin/evaluations?status=in_progress')}
          />
          <TotalCard
            icon={FileText}
            label={t('suites.totals.reports')}
            value={tt.reports}
            tone="accent"
            testid="suites-total-reports"
            onClick={() => navigate('/admin/reports')}
          />
          <TotalCard
            icon={Gauge}
            label={t('suites.totals.released')}
            value={tt.released}
            tone="info"
            testid="suites-total-released"
            onClick={() => navigate('/admin/reports?status=released_to_client')}
          />
        </div>

        {/* Suite cards */}
        <div className="grid md:grid-cols-2 gap-5">
          {shownSuites.map((s) => {
            const def = SUITE_DEF[s.code] || {};
            const Icon = def.icon || Layers;
            return (
              <Card
                key={s.code}
                data-testid={`suite-card-${s.code}`}
                role="button"
                tabIndex={0}
                onClick={() => def.route && navigate(def.route)}
                onKeyDown={(e) => {
                  if (def.route && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    navigate(def.route);
                  }
                }}
                className="p-6 shadow-card hover:shadow-medium transition-smooth cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/40"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${TONE_BG[def.tone] || TONE_BG.primary}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="font-display font-bold text-lg tracking-tight">{s.name}</h3>
                      <p className="text-xs text-muted-foreground">{s.tagline}</p>
                    </div>
                  </div>
                  <Badge className={`${STATUS_TONE[s.status]} capitalize`}>{t(`common.${s.status}`, s.status)}</Badge>
                </div>

                <div className="grid grid-cols-3 gap-3 mt-5">
                  <MiniStat label={t('suites.stats.completion')} value={`${s.completion_pct}%`} />
                  <MiniStat label={t('suites.stats.avgScore')} value={s.avg_score !== null ? s.avg_score : '—'} />
                  <MiniStat label={t('suites.stats.timeToRelease')} value={fmtHours(s.avg_time_to_release_hours)} />
                  <MiniStat label={t('suites.stats.questionnaires')} value={`${s.active_questionnaires}/${s.total_questionnaires}`} />
                  <MiniStat label={t('suites.stats.released')} value={s.reports_released} />
                  <MiniStat label={t('suites.stats.pending')} value={s.reports_pending} />
                </div>

                <div className="mt-5">
                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-1.5">
                    <span>{s.assignments_submitted} / {s.assignments_total} {t('suites.totals.assignments').toLowerCase()}</span>
                    <span className="font-semibold">{s.completion_pct}%</span>
                  </div>
                  <Progress value={s.completion_pct} className="h-2" />
                </div>

                <div className="mt-5 flex items-center justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); if (def.route) navigate(def.route); }}
                    className="gap-1.5"
                    data-testid={`suite-card-${s.code}-open`}
                  >
                    {t('suites.openConsole')} <ArrowRight className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </Card>
            );
          })}
          {shownSuites.length === 0 && (
            <Card className="p-12 text-center text-sm text-muted-foreground md:col-span-2">
              {t('suites.noMatch')}
            </Card>
          )}
        </div>

        {/* Comparison table */}
        <Card className="shadow-card overflow-hidden" data-testid="suites-comparison-card">
          <div
            role="button"
            tabIndex={0}
            data-testid="suites-comparison-toggle"
            aria-expanded={compareOpen}
            onClick={() => setCompareOpen((v) => !v)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setCompareOpen((v) => !v); }
            }}
            className="w-full px-6 py-5 border-b border-border flex items-center justify-between gap-3 hover:bg-muted/30 transition-smooth text-left cursor-pointer"
          >
            <div className="flex items-center gap-2">
              {compareOpen ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
              <div>
                <h3 className="font-display font-bold text-lg tracking-tight">{t('suites.comparison')}</h3>
                <p className="text-sm text-muted-foreground">{t('suites.comparisonSubtitle')}</p>
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5"
              data-testid="suites-csv-download"
              onClick={(e) => { e.stopPropagation(); if (shownSuites.length) downloadCsv(shownSuites, data?.filters_applied, t, i18n.language); }}
              disabled={!shownSuites.length}
            >
              <Download className="h-4 w-4" /> {t('common.downloadCsv')}
            </Button>
          </div>
          {compareOpen && (
          <div className="overflow-x-auto" data-testid="suites-comparison-table">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-6 py-3"><SortableHeader sort={suiteSort} k="suite">{t('suites.csv.headers.suite')}</SortableHeader></th>
                  <th className="px-6 py-3"><SortableHeader sort={suiteSort} k="status">{t('suites.csv.headers.status')}</SortableHeader></th>
                  <th className="px-6 py-3 text-right"><SortableHeader sort={suiteSort} k="activeQs">{t('suites.csv.headers.activeQs')}</SortableHeader></th>
                  <th className="px-6 py-3 text-right"><SortableHeader sort={suiteSort} k="assignments">{t('suites.csv.headers.assignments')}</SortableHeader></th>
                  <th className="px-6 py-3 text-right"><SortableHeader sort={suiteSort} k="completion">{t('suites.csv.headers.completion')}</SortableHeader></th>
                  <th className="px-6 py-3 text-right"><SortableHeader sort={suiteSort} k="reports">{t('suites.totals.reports')}</SortableHeader></th>
                  <th className="px-6 py-3 text-right"><SortableHeader sort={suiteSort} k="released">{t('suites.csv.headers.released')}</SortableHeader></th>
                  <th className="px-6 py-3 text-right"><SortableHeader sort={suiteSort} k="pending">{t('suites.csv.headers.pending')}</SortableHeader></th>
                  <th className="px-6 py-3 text-right"><SortableHeader sort={suiteSort} k="avgScore">{t('suites.csv.headers.avgScore')}</SortableHeader></th>
                  <th className="px-6 py-3 text-right"><SortableHeader sort={suiteSort} k="ttr">{t('suites.csv.headers.ttr')}</SortableHeader></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {shownSuites.map((s) => {
                  const def = SUITE_DEF[s.code] || {};
                  return (
                    <tr key={s.code} data-testid={`suite-row-${s.code}`} className="hover:bg-muted/20 cursor-pointer" onClick={() => def.route && navigate(def.route)}>
                      <td className="px-6 py-3">
                        <p className="font-medium">{s.name}</p>
                        <p className="text-xs text-muted-foreground">{s.tagline}</p>
                      </td>
                      <td className="px-6 py-3"><Badge className={`${STATUS_TONE[s.status]} capitalize`}>{t(`common.${s.status}`, s.status)}</Badge></td>
                      <td className="px-6 py-3 text-right tabular-nums">{s.active_questionnaires}</td>
                      <td className="px-6 py-3 text-right tabular-nums">{s.assignments_total}</td>
                      <td className="px-6 py-3 text-right tabular-nums">{s.completion_pct}%</td>
                      <td className="px-6 py-3 text-right tabular-nums">{s.reports_total}</td>
                      <td className="px-6 py-3 text-right tabular-nums">{s.reports_released}</td>
                      <td className="px-6 py-3 text-right tabular-nums">{s.reports_pending}</td>
                      <td className="px-6 py-3 text-right tabular-nums">{s.avg_score ?? '—'}</td>
                      <td className="px-6 py-3 text-right tabular-nums">{fmtHours(s.avg_time_to_release_hours)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          )}
        </Card>

        {/* Recent activity */}
        <Card className="shadow-card overflow-hidden">
          <div className="px-6 py-5 border-b border-border flex items-center justify-between">
            <div>
              <h3 className="font-display font-bold text-lg tracking-tight">{t('suites.recentActivity')}</h3>
              <p className="text-sm text-muted-foreground">{t('suites.recentActivitySubtitle')}</p>
            </div>
            {(data?.filters_applied?.company_id || data?.filters_applied?.date_from || data?.filters_applied?.date_to) && (
              <Badge variant="outline" className="gap-1.5"><Building2 className="h-3 w-3" /> {t('suites.filtered')}</Badge>
            )}
          </div>
          {shownActivity.length === 0 ? (
            <div className="px-6 py-10 text-sm text-center text-muted-foreground">{t('suites.noActivity')}</div>
          ) : (
            <ul className="divide-y divide-border max-h-[420px] overflow-y-auto">
              {shownActivity.map((ev, idx) => {
                const def = SUITE_DEF[ev.suite_code] || {};
                const Icon = def.icon || Layers;
                const tone = ev.type === 'report'
                  ? (REPORT_STATUS_TONE[ev.status] || 'bg-muted text-muted-foreground')
                  : TONE_BG[def.tone] || 'bg-muted text-muted-foreground';
                return (
                  <li key={`${ev.type}-${ev.id}-${idx}`} data-testid={`activity-${ev.type}-${ev.id}`} className="px-6 py-3 flex items-center gap-4">
                    <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${TONE_BG[def.tone] || TONE_BG.primary}`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{def.label || ev.suite_code || '—'}</span>
                        <span className="text-xs text-muted-foreground">·</span>
                        <span className="text-xs capitalize text-muted-foreground">{ev.type}</span>
                      </div>
                      <p className="text-sm truncate">{ev.title || '—'}</p>
                    </div>
                    <div className="text-right">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${tone}`}>
                        {ev.type === 'report' ? (t(`reports.status.${ev.status}`, ev.status)) : (ev.status || '—').replace(/_/g, ' ')}
                      </span>
                      {ev.score !== undefined && ev.score !== null && (
                        <p className="text-xs text-muted-foreground mt-0.5">{t('common.score').toLowerCase()} {ev.score}</p>
                      )}
                      <p className="text-xs text-muted-foreground mt-0.5">{fmtRelative(ev.timestamp, t)}</p>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </div>
    </>
  );
}

function TotalCard({ icon: Icon, label, value, tone, sub, onClick, testid }) {
  const clickable = typeof onClick === 'function';
  return (
    <Card
      data-testid={testid}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick(e);
              }
            }
          : undefined
      }
      className={`p-5 shadow-card ${clickable ? 'cursor-pointer hover:shadow-medium transition-smooth focus:outline-none focus:ring-2 focus:ring-primary/40' : ''}`}
    >
      <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${TONE_BG[tone] || TONE_BG.primary}`}>
        <Icon className="h-5 w-5" />
      </div>
      <p className="text-sm font-medium text-muted-foreground mt-4">{label}</p>
      <p className="text-3xl font-display font-bold mt-0.5 tabular-nums">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </Card>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="p-3 rounded-lg bg-muted/40 border border-border/60">
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="font-semibold mt-0.5 tabular-nums">{value}</p>
    </div>
  );
}

// ---------------- CSV export ----------------
function downloadCsv(suites, filtersApplied, t, locale) {
  const H = t || ((k) => k);
  const headers = [
    H('suites.csv.headers.suite'),
    H('suites.csv.headers.status'),
    H('suites.csv.headers.completion'),
    H('suites.csv.headers.avgScore'),
    H('suites.csv.headers.ttr'),
    H('suites.csv.headers.activeQs'),
    H('suites.csv.headers.totalQs'),
    H('suites.csv.headers.assignments'),
    H('suites.csv.headers.released'),
    H('suites.csv.headers.pending'),
  ];
  const statusText = (s) => H(`common.${s}`) || s;
  const rows = suites.map((s) => [
    s.name,
    statusText(s.status),
    s.completion_pct,
    s.avg_score ?? '',
    // Reuse local fmt
    ((h) => {
      if (h === null || h === undefined) return '—';
      if (h < 1) return `${Math.round(h * 60)} min`;
      if (h < 48) return `${h.toFixed(1)} h`;
      return `${(h / 24).toFixed(1)} d`;
    })(s.avg_time_to_release_hours),
    s.active_questionnaires,
    s.total_questionnaires,
    s.assignments_total,
    s.reports_released,
    s.reports_pending,
  ]);

  const escape = (v) => {
    const str = v === null || v === undefined ? '' : String(v);
    if (/[",\n]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
    return str;
  };
  const csv = [headers, ...rows].map((r) => r.map(escape).join(',')).join('\r\n');

  const preamble = [];
  preamble.push(H('suites.csv.title'));
  preamble.push(`${H('suites.csv.generated')}: ${new Date().toISOString()}`);
  if (filtersApplied?.company_id) preamble.push(`${H('common.company')}: ${filtersApplied.company_id}`);
  if (filtersApplied?.date_from) preamble.push(`${H('common.from')}: ${filtersApplied.date_from}`);
  if (filtersApplied?.date_to) preamble.push(`${H('common.to')}: ${filtersApplied.date_to}`);
  const full = preamble.map((l) => `# ${l}`).join('\r\n') + '\r\n\r\n' + csv + '\r\n';

  const blob = new Blob(['\uFEFF' + full], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const stamp = new Date().toISOString().slice(0, 10);
  const fname = (locale || '').startsWith('es')
    ? `HM_Suites_Comparacion_${stamp}.csv`
    : `HM_Suites_Overview_${stamp}.csv`;
  a.href = url;
  a.download = fname;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
